import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Landing Page Components
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Benefits from './components/Benefits';
import Stats from './components/Stats';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import NikoChatbot from './components/NikoChatbot';
import ChatFAB from './components/ChatFAB';
import ProfilePage from './components/ProfilePage';

// Owner Portal Components
import OwnerLayout from './components/owner/OwnerLayout';
import OwnerDashboard from './components/owner/OwnerDashboard';
import FacilitiesPage from './components/owner/FacilitiesPage';
import AnalyticsPage from './components/owner/AnalyticsPage';
import BillingPage from './components/owner/BillingPage';
import UsersPage from './components/owner/UsersPage';
import SupportPage from './components/owner/SupportPage';
import ContentPage from './components/owner/ContentPage';
import LegalPage from './components/owner/LegalPage';
import SettingsPage from './components/owner/SettingsPage';

// Hospital Portal Components
import HospitalLayout from './components/hospital/HospitalLayout';
import HospitalDashboard from './components/hospital/HospitalDashboard';
import SafetyEventsPage from './components/hospital/SafetyEventsPage';
import AuditsPage from './components/hospital/AuditsPage';
import RiskRegisterPage from './components/hospital/RiskRegisterPage';
import PIProjectsPage from './components/hospital/PIProjectsPage';
import InfectionControlPage from './components/hospital/InfectionControlPage';
import PoliciesPage from './components/hospital/PoliciesPage';
import TrainingPage from './components/hospital/TrainingPage';
import HospitalSettingsPage from './components/hospital/SettingsPage';
import SafetyIntelligencePage from './components/hospital/SafetyIntelligencePage';
import ReportEventPage from './components/hospital/ReportEventPage';
import ActionPlansPage from './components/hospital/ActionPlansPage';
import EventDetailPage from './components/hospital/EventDetailPage';
import AuditDetailPage from './components/hospital/AuditDetailPage';
import RiskDetailPage from './components/hospital/RiskDetailPage';

// Landing Page Component
const LandingPage = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header onLoginClick={() => setIsLoginOpen(true)} />
      <main>
        <Hero 
          onDemoClick={() => {
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          }} 
          onChatOpen={() => setIsChatOpen(true)}
        />
        <Features />
        <Stats />
        <Benefits />
        <Testimonials />
        <Contact />
      </main>
      <Footer />

      {/* Modals */}
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
      
      {/* Chatbot */}
      <ChatFAB onClick={() => setIsChatOpen(true)} isOpen={isChatOpen} />
      <NikoChatbot isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
};

// Owner Portal Wrapper
const OwnerPortal = ({ children }) => {
  return (
    <OwnerLayout>
      {children}
    </OwnerLayout>
  );
};

// Hospital Portal Wrapper
const HospitalPortal = ({ children }) => {
  return (
    <HospitalLayout>
      {children}
    </HospitalLayout>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Profile Page */}
          <Route path="/profile" element={<ProfilePage />} />
          
          {/* Owner Portal Routes */}
          <Route path="/owner" element={<OwnerPortal><OwnerDashboard /></OwnerPortal>} />
          <Route path="/owner/facilities" element={<OwnerPortal><FacilitiesPage /></OwnerPortal>} />
          <Route path="/owner/analytics" element={<OwnerPortal><AnalyticsPage /></OwnerPortal>} />
          <Route path="/owner/billing" element={<OwnerPortal><BillingPage /></OwnerPortal>} />
          <Route path="/owner/users" element={<OwnerPortal><UsersPage /></OwnerPortal>} />
          <Route path="/owner/support" element={<OwnerPortal><SupportPage /></OwnerPortal>} />
          <Route path="/owner/content" element={<OwnerPortal><ContentPage /></OwnerPortal>} />
          <Route path="/owner/legal" element={<OwnerPortal><LegalPage /></OwnerPortal>} />
          <Route path="/owner/settings" element={<OwnerPortal><SettingsPage /></OwnerPortal>} />
          
          {/* Hospital Portal Routes */}
          <Route path="/hospital" element={<HospitalPortal><HospitalDashboard /></HospitalPortal>} />
          <Route path="/hospital/safety-events" element={<HospitalPortal><SafetyEventsPage /></HospitalPortal>} />
          <Route path="/hospital/safety-events/:eventId" element={<HospitalPortal><EventDetailPage /></HospitalPortal>} />
          <Route path="/hospital/audits" element={<HospitalPortal><AuditsPage /></HospitalPortal>} />
          <Route path="/hospital/audits/:auditId" element={<HospitalPortal><AuditDetailPage /></HospitalPortal>} />
          <Route path="/hospital/risk-register" element={<HospitalPortal><RiskRegisterPage /></HospitalPortal>} />
          <Route path="/hospital/risk-register/:riskId" element={<HospitalPortal><RiskDetailPage /></HospitalPortal>} />
          <Route path="/hospital/pi-projects" element={<HospitalPortal><PIProjectsPage /></HospitalPortal>} />
          <Route path="/hospital/infection-control" element={<HospitalPortal><InfectionControlPage /></HospitalPortal>} />
          <Route path="/hospital/policies" element={<HospitalPortal><PoliciesPage /></HospitalPortal>} />
          <Route path="/hospital/training" element={<HospitalPortal><TrainingPage /></HospitalPortal>} />
          <Route path="/hospital/safety-intelligence" element={<HospitalPortal><SafetyIntelligencePage /></HospitalPortal>} />
          <Route path="/hospital/report-event" element={<HospitalPortal><ReportEventPage /></HospitalPortal>} />
          <Route path="/hospital/action-plans" element={<HospitalPortal><ActionPlansPage /></HospitalPortal>} />
          <Route path="/hospital/settings" element={<HospitalPortal><HospitalSettingsPage /></HospitalPortal>} />
          
          {/* Fallback */}
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast Notifications */}
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
